import {
  Container,
  Dialog,
  Typography,
  Grid,
  Toolbar,
  AppBar,
  IconButton,
  List,
  Divider,
  ListItem,
  makeStyles,
  Box,
} from "@material-ui/core";
import { IoIosClose } from "react-icons/io";
import { useEffect } from "react";
import { Formik, useFormikContext } from "formik";

import { history } from "../../..";
import { ReceiveDialogProps } from "../../../interface/dialog/ReceiveDialogProps";
import { ReceiveIncompleteTicketFormValues } from "../../../interface/formik/ReceiveIncompleteTicketFormValues";
import FormTextField from "../form-text-field";
import FormTruckLicensePlateField from "../form-truck-license-plate-field";
import FormTextReadOnlyField from "../form-text-read-only-field";
import Button from "../Button";
import PriceField from "../price-field";
import useFocusSearch from "../../../hooks/useFocusSearch";

const useStyles = makeStyles({
  link: {
    color: "blue",
    textDecoration: "underline",
    cursor: "pointer",
  },
  divider: { marginTop: "5px", marginBottom: "15px" },
  appBar: {
    position: "relative",
  },
  appBarTitle: {
    flex: 1,
  },
  contentList: {
    padding: "24px",
  },
  sectionItem: {
    padding: "0 0 16px",
  },
  actionsRow: {
    display: "flex",
    justifyContent: "flex-end",
    paddingTop: "8px",
  },
  actionButton: {
    width: 180,
    maxWidth: "100%",
  },
});

const ReceiveDialog = ({
  open,
  onClose,
  selectedTicket,
  onReceiveTicket,
  trucks,
}: ReceiveDialogProps) => {
  const classes = useStyles();
  const { doFocusSearchInput } = useFocusSearch("ticketNumber");

  const receiveIncompleteTicketFormValues: ReceiveIncompleteTicketFormValues = {
    jobSite: selectedTicket ? selectedTicket.jobSite : "",
    siteContactName: selectedTicket ? selectedTicket.siteContactName : "",
    transportCompanyName: selectedTicket
      ? selectedTicket.transportCompanyName
      : "",
    truckDriverName: selectedTicket ? selectedTicket.truckDriverName : "",
    truckLicensePlate: selectedTicket
      ? selectedTicket.truckLicensePlate?.trim()?.replace(/\s/g, "")
      : "",
    truckNumber: selectedTicket ? selectedTicket.truckNumber : "",
    salePrice: selectedTicket ? selectedTicket.salePrice : null,
  };

  const TicketCompleteView = () => {
    if (!selectedTicket) return <></>;
    return (
      <>
        <ListItem className={classes.sectionItem}>
          <span>
            Ticket is complete. If you need to edit the ticket data please click{" "}
            <span
              onClick={() => {
                if (selectedTicket) {
                  history.push(`/ticket/${selectedTicket.id}`);
                }
              }}
              className={classes.link}
            >
              here
            </span>
            .
          </span>
        </ListItem>
        <Divider className={classes.divider} />
        <ListItem className={classes.sectionItem}>
          <Grid container spacing={3}>
            {/* Row 1: Job Site + Contractor + Haulage Co. */}
            <Grid item xs={12} sm={4}>
              <FormTextReadOnlyField
                label={"Job Site"}
                value={selectedTicket.jobSite}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormTextReadOnlyField
                label={"Contractor"}
                value={selectedTicket.contractorCompanyName}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormTextReadOnlyField
                label={"Haulage Co."}
                value={selectedTicket.transportCompanyName}
              />
            </Grid>
            {/* Row 2: License Plate + Truck Number + Driver Name */}
            <Grid item xs={12} sm={4}>
              <FormTextReadOnlyField
                label={"License Plate"}
                value={selectedTicket.truckLicensePlate}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormTextReadOnlyField
                label={"Truck Number"}
                value={selectedTicket.truckNumber}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormTextReadOnlyField
                label={"Driver Name"}
                value={selectedTicket.truckDriverName}
              />
            </Grid>
            {/* Row 3: Sale Price */}
            <Grid item xs={12} sm={4}>
              <FormTextReadOnlyField
                label={"Sale Price"}
                value={selectedTicket.salePrice != null ? `$ ${selectedTicket.salePrice}` : ""}
              />
            </Grid>
          </Grid>
        </ListItem>
      </>
    );
  };

  const TicketIncompleteView = () => {
    if (!selectedTicket) return <></>;

    const { values, setFieldValue } = useFormikContext<ReceiveIncompleteTicketFormValues>();
    return (
      <>
        <ListItem className={classes.sectionItem}>
          Ticket is missing required information please review the ticket data
          below. You can still receive the ticket with missing information.
        </ListItem>
        <Divider className={classes.divider} />
        <ListItem className={classes.sectionItem}>
          <form>
            <Grid container spacing={3}>
              {/* Row 1: Job Site + Site Contact + Contractor */}
              <Grid item xs={12} sm={4}>
                <FormTextField
                  name={"jobSite"}
                  label={"Job Site"}
                  value={values.jobSite}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormTextField
                  name={"siteContactName"}
                  label={"Site Contact"}
                  value={values.siteContactName}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormTextField
                  name={"transportCompanyName"}
                  label={"Haulage Co."}
                  value={values.transportCompanyName}
                  disabled={
                    selectedTicket &&
                    Number(selectedTicket.transportCompanyId) > 0
                  }
                />
              </Grid>
              {/* Row 2: License Plate + Truck Number + Driver Name */}
              <Grid item xs={12} sm={4}>
                <FormTruckLicensePlateField
                  value={values.truckLicensePlate}
                  name={"truckLicensePlate"}
                  label={"Truck License Plate"}
                  placeholder={null}
                  disabled={
                    selectedTicket && Number(selectedTicket.truckId) > 0
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormTextField
                  value={values.truckNumber}
                  name={"truckNumber"}
                  label={"Truck Number"}
                  disabled={
                    selectedTicket && Number(selectedTicket.truckId) > 0
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormTextField
                  name={"truckDriverName"}
                  label={"Driver Name"}
                  value={values.truckDriverName}
                />
              </Grid>
              {/* Row 3: Sale Price */}
              <Grid item xs={12} sm={4}>
                <PriceField
                  label="Sale Price"
                  value={values.salePrice}
                  onChange={(val) => setFieldValue("salePrice", val)}
                />
              </Grid>
            </Grid>
          </form>
        </ListItem>
      </>
    );
  };

  const TicketBody = () => {
    const { values, setFieldValue } =
      useFormikContext<ReceiveIncompleteTicketFormValues>();

    useEffect(() => {
      if (!values.truckLicensePlate) return;

      if (
        !values.truckLicensePlate?.includes("_") &&
        values.truckLicensePlate?.length >= 7
      ) {
        const truck = trucks.find(
          (t) =>
            t.licensePlate &&
            t.licensePlate.replace(/\s/g, "").toLocaleLowerCase() ===
              values.truckLicensePlate.replace(/\s/g, "").toLocaleLowerCase()
        );
        if (truck) {
          setFieldValue("truckNumber", truck?.number);
          setFieldValue("truckDriverName", truck?.driverName);
          setFieldValue("transportCompanyName", truck?.transportCompanyName);
        }
      }
    }, [values.truckLicensePlate]);

    return (
      <>
        <AppBar className={classes.appBar}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => onClose()}
              aria-label="close"
            >
              <IoIosClose />
            </IconButton>
            <Typography
              className={classes.appBarTitle}
              variant="h6"
              component="div"
            >
              Receive Ticket {selectedTicket ? `#${selectedTicket.ticketNumber}` : ""}
            </Typography>
          </Toolbar>
        </AppBar>
        <Container maxWidth="lg">
          <List className={classes.contentList}>
            {selectedTicket && selectedTicket.isComplete ? (
              <TicketCompleteView />
            ) : (
              <TicketIncompleteView />
            )}
            <ListItem className={classes.sectionItem}>
              <Box className={classes.actionsRow}>
                <div className={classes.actionButton}>
                  <Button
                    label="Receive Ticket"
                    onClick={() => {
                      if (selectedTicket && !selectedTicket.isComplete) {
                        onReceiveTicket(values);
                      } else {
                        onReceiveTicket();
                      }
                      doFocusSearchInput();
                    }}
                    autoFocus
                    color="primary"
                    variant="contained"
                    type="button"
                  />
                </div>
              </Box>
            </ListItem>
          </List>
        </Container>
      </>
    );
  };

  return (
    <Dialog fullScreen open={open} onClose={() => onClose()}>
      <Formik
        initialValues={receiveIncompleteTicketFormValues}
        onSubmit={() => {}}
      >
        <TicketBody />
      </Formik>
    </Dialog>
  );
};

export default ReceiveDialog;
